import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createOutOfOfficeEvent, CreateOutOfOfficeEventBody, UpdateOutOfOfficeEventBody, getOutOfOfficeEvents, deleteOutOfOfficeEvent, updateOutOfOfficeEvent, getActiveOutOfOffice } from '@uplift/api';
import { providerQueryKeys } from './providerQueryKeys';
import { BaseQueryOpts } from '../baseTypes';

export const useProviderOutOfOfficeQuery = (providerId: string) => {
  return useQuery({
    queryKey: providerQueryKeys.outOfOffice(providerId),
    queryFn: async () => {
      const response = await getOutOfOfficeEvents(providerId);

      return response.data;
    },
  });
};

export const useCreateProviderOutOfOfficeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { providerId: string } & CreateOutOfOfficeEventBody) => {
      const { providerId, ...body } = args;

      return createOutOfOfficeEvent(providerId, body);
    },
    async onSuccess(_data, variables) {
      await queryClient.invalidateQueries(providerQueryKeys.outOfOffice(variables.providerId));
    },
  });
};

export const useUpdateProviderOutOfOfficeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { providerId: string; outOfOfficeId: string } & UpdateOutOfOfficeEventBody) => {
      const { providerId, outOfOfficeId, ...body } = args;

      return updateOutOfOfficeEvent(providerId, outOfOfficeId, body);
    },
    async onSuccess(_data, variables) {
      await queryClient.invalidateQueries(providerQueryKeys.outOfOffice(variables.providerId));
    },
  });
};

export const useDeleteProviderOutOfOfficeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, outOfOfficeId }: { providerId: string; outOfOfficeId: string }) => {
      return await deleteOutOfOfficeEvent(providerId, outOfOfficeId);
    },
    async onSuccess(_data, variables) {
      await queryClient.invalidateQueries(providerQueryKeys.outOfOffice(variables.providerId));
    },
  });
};


export const useActiveOutOfOfficeQuery = (providerId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ ...providerQueryKeys.outOfOffice(providerId), 'current' ],
    queryFn: async () => {
      if (!providerId) return null;

      const response = await getActiveOutOfOffice(providerId);

      return response.data;
    },
    ...opts,
  });
};
